import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, Button, TextField, MenuItem,
  CircularProgress, Chip, Tooltip
} from '@mui/material';
import { ArrowBack, Add, Delete } from '@mui/icons-material';
import axios from 'axios';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import { useLocations } from '../hooks/useLocations';

const ENTRY_TYPES = ['Positive Adjustment', 'Negative Adjustment', 'Transfer'];

const emptyLine = () => ({
  _key:           Date.now() + Math.random(),
  entryType:      'Positive Adjustment',
  itemNo:         '',
  description:    '',
  locationCode:   '',
  toLocationCode: '',
  quantity:       '',
  unitCost:       '',
  amount:         0,
  inventoryGL:    '',
  adjustmentGL:   ''
});

const ENTRY_COLOR = {
  'Positive Adjustment': 'success',
  'Negative Adjustment': 'error',
  'Transfer':            'info'
};

const ItemJournalForm = () => {
  const navigate      = useNavigate();
  const { journalNo } = useParams();
  const isEdit        = Boolean(journalNo);

  const [header,  setHeader]  = useState({
    journalDate: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [lines,   setLines]   = useState([emptyLine()]);
  const [items,   setItems]   = useState([]);
  const [stockMap,setStockMap]= useState({});
  const [error,   setError]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [posted,  setPosted]  = useState(false);

  const { accounts: glAccounts } = useGLAccounts();
  const { locations }            = useLocations();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    const load = async () => {
      try {
        const [itemsRes, stockRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/payables/items`,      { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/payables/item-stock`, { headers })
        ]);
        setItems(itemsRes.data.filter(i => i.itemType === 'Item'));
        setStockMap(stockRes.data);

        if (isEdit) {
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/payables/item-journals/${journalNo}`,
            { headers }
          );
          setHeader({
            journalDate: data.journalDate ? data.journalDate.split('T')[0] : '',
            description: data.description || ''
          });
          setLines(data.lines.map(l => ({ ...l, _key: Date.now() + Math.random() })));
          setPosted(data.status === 'Posted');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [journalNo]); // eslint-disable-line

  const stockAt = (itemNo, locationCode) => {
    const s = stockMap[itemNo];
    if (!s) return null;
    if (!locationCode) return { qty: s.qty || 0 };
    return s.byLocation?.[locationCode] || { qty: 0 };
  };

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const updated = { ...l, [field]: value };

      if (field === 'itemNo') {
        const item = items.find(i => i.itemNo === value);
        if (item) {
          const s       = stockMap[value];
          const avgCost = s && s.qty > 0 ? s.value / s.qty : item.unitPrice || 0;
          updated.description  = item.itemName;
          updated.inventoryGL  = item.glAccount;
          updated.unitCost     = avgCost.toFixed(4);
          updated.amount       = (parseFloat(updated.quantity) || 0) * avgCost;
        }
      }

      if (field === 'entryType') {
        updated.toLocationCode = '';
        updated.adjustmentGL   = '';
      }

      if (field === 'quantity' || field === 'unitCost') {
        const qty  = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
        const cost = parseFloat(field === 'unitCost'  ? value : updated.unitCost)  || 0;
        updated.amount = qty * cost;
      }

      return updated;
    }));
  };

  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => prev.filter(l => l._key !== key));

  const handleSave = async () => {
    setError('');
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.itemNo)                                { setError(`Line ${i + 1}: Item is required`);           return; }
      if (!l.locationCode)                          { setError(`Line ${i + 1}: Location is required`);       return; }
      if (!l.quantity || parseFloat(l.quantity) <= 0) { setError(`Line ${i + 1}: Quantity must be > 0`);    return; }
      if (!l.inventoryGL)                           { setError(`Line ${i + 1}: Inventory GL is required`);   return; }
      if (!l.adjustmentGL)                          { setError(`Line ${i + 1}: ${l.entryType === 'Transfer' ? 'To-Location Inventory GL' : 'Adjustment GL'} is required`); return; }
      if (l.entryType === 'Transfer' && !l.toLocationCode)
        { setError(`Line ${i + 1}: To-Location is required for Transfer`); return; }
    }

    setSaving(true);
    try {
      const payload = {
        ...header,
        lines: lines.map((l, i) => ({
          lineNo:         i + 1,
          entryType:      l.entryType,
          itemNo:         l.itemNo,
          description:    l.description,
          locationCode:   l.locationCode,
          toLocationCode: l.toLocationCode || '',
          quantity:       parseFloat(l.quantity),
          unitCost:       parseFloat(l.unitCost) || 0,
          amount:         l.amount || 0,
          inventoryGL:    l.inventoryGL,
          adjustmentGL:   l.adjustmentGL
        }))
      };

      if (isEdit) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/payables/item-journals/${journalNo}`,
          payload, { headers }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/payables/item-journals`,
          payload, { headers }
        );
      }
      navigate('/payables/item-journals');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save journal');
    } finally {
      setSaving(false);
    }
  };


  if (loading) return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/payables/item-journals')} sx={{ mr: 2 }}>
              Back
            </Button>
            <Box flex={1}>
              <Typography variant="h5" color="primary">
                {isEdit ? `Item Journal: ${journalNo}` : 'New Item Journal'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Positive Adjustment — increase stock &nbsp;|&nbsp;
                Negative Adjustment — decrease stock &nbsp;|&nbsp;
                Transfer — move between locations
              </Typography>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Header */}
          <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
            <TextField label="Journal Date" type="date" size="small"
              value={header.journalDate}
              onChange={e => setHeader({ ...header, journalDate: e.target.value })}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }}
              disabled={posted} />
            <TextField label="Description / Memo" size="small"
              value={header.description}
              onChange={e => setHeader({ ...header, description: e.target.value })}
              sx={{ minWidth: 320, flex: 1 }} disabled={posted} />
          </Box>

          {/* Lines */}
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ mb: 2, minWidth: 1100 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ minWidth: 160 }}><strong>Entry Type</strong></TableCell>
                  <TableCell sx={{ minWidth: 170 }}><strong>Item</strong></TableCell>
                  <TableCell sx={{ minWidth: 130 }}><strong>Description</strong></TableCell>
                  <TableCell sx={{ minWidth: 150 }}><strong>From Location</strong></TableCell>
                  <TableCell sx={{ minWidth: 100 }} align="right"><strong>Stock</strong></TableCell>
                  <TableCell sx={{ minWidth: 150 }}><strong>To Location</strong></TableCell>
                  <TableCell sx={{ minWidth: 90 }} align="right"><strong>Qty</strong></TableCell>
                  <TableCell sx={{ minWidth: 100 }} align="right"><strong>Unit Cost</strong></TableCell>
                  <TableCell sx={{ minWidth: 160 }}><strong>Inventory GL</strong></TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Tooltip title="For Adjustments: the contra account (e.g. Inventory Adjustment, Opening Stock). For Transfer: the destination inventory GL account.">
                      <strong>Adj / To-GL</strong>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}><strong>Amount</strong></TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map(line => {
                  const stk = stockAt(line.itemNo, line.locationCode);
                  return (
                    <TableRow key={line._key}>
                      {/* Entry Type */}
                      <TableCell>
                        {posted
                          ? <Chip label={line.entryType} color={ENTRY_COLOR[line.entryType] || 'default'} size="small" variant="outlined" />
                          : <TextField select size="small" value={line.entryType} fullWidth
                              onChange={e => updateLine(line._key, 'entryType', e.target.value)}>
                              {ENTRY_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </TextField>
                        }
                      </TableCell>

                      {/* Item */}
                      <TableCell>
                        {posted
                          ? line.itemNo
                          : <TextField select size="small" value={line.itemNo} fullWidth
                              onChange={e => updateLine(line._key, 'itemNo', e.target.value)}>
                              <MenuItem value=""><em>Select Item</em></MenuItem>
                              {items.map(i => (
                                <MenuItem key={i.itemNo} value={i.itemNo}>
                                  {i.itemNo} – {i.itemName}
                                </MenuItem>
                              ))}
                            </TextField>
                        }
                      </TableCell>

                      {/* Description */}
                      <TableCell>
                        {posted
                          ? <Typography variant="body2">{line.description}</Typography>
                          : <TextField size="small" value={line.description} fullWidth
                              onChange={e => updateLine(line._key, 'description', e.target.value)} />
                        }
                      </TableCell>

                      {/* From Location */}
                      <TableCell>
                        {posted
                          ? <Chip label={line.locationCode || '—'} size="small" variant="outlined" />
                          : <TextField select size="small" value={line.locationCode} fullWidth
                              onChange={e => updateLine(line._key, 'locationCode', e.target.value)}>
                              <MenuItem value=""><em>Select</em></MenuItem>
                              {locations.map(loc => (
                                <MenuItem key={loc.locationCode} value={loc.locationCode}>
                                  {loc.locationCode} – {loc.locationName}
                                </MenuItem>
                              ))}
                            </TextField>
                        }
                      </TableCell>

                      {/* Stock at from-location */}
                      <TableCell align="right">
                        {line.itemNo && line.locationCode ? (
                          <Chip
                            label={(stk?.qty || 0).toFixed(2)}
                            color={(stk?.qty || 0) > 0 ? 'success' : 'error'}
                            size="small" variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* To Location (Transfer only) */}
                      <TableCell>
                        {line.entryType === 'Transfer' ? (
                          posted
                            ? <Chip label={line.toLocationCode || '—'} size="small" variant="outlined" />
                            : <TextField select size="small" value={line.toLocationCode} fullWidth
                                onChange={e => updateLine(line._key, 'toLocationCode', e.target.value)}>
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {locations
                                  .filter(l => l.locationCode !== line.locationCode)
                                  .map(loc => (
                                    <MenuItem key={loc.locationCode} value={loc.locationCode}>
                                      {loc.locationCode} – {loc.locationName}
                                    </MenuItem>
                                  ))}
                              </TextField>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Quantity */}
                      <TableCell align="right">
                        {posted
                          ? line.quantity
                          : <TextField size="small" type="number" value={line.quantity} fullWidth
                              inputProps={{ min: 0.001, step: '0.001' }}
                              onChange={e => updateLine(line._key, 'quantity', e.target.value)} />
                        }
                      </TableCell>

                      {/* Unit Cost */}
                      <TableCell align="right">
                        {posted
                          ? (line.unitCost || 0).toFixed(2)
                          : <TextField size="small" type="number" value={line.unitCost} fullWidth
                              inputProps={{ min: 0, step: '0.01' }}
                              onChange={e => updateLine(line._key, 'unitCost', e.target.value)} />
                        }
                      </TableCell>

                      {/* Inventory GL (auto, read-only) */}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {line.inventoryGL || <em style={{ color: '#bbb' }}>auto</em>}
                        </Typography>
                      </TableCell>

                      {/* Adjustment / To-Location GL */}
                      <TableCell>
                        {posted
                          ? <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{line.adjustmentGL}</Typography>
                          : <TextField select size="small" value={line.adjustmentGL} fullWidth
                              label={line.entryType === 'Transfer' ? 'To-Location GL' : 'Adjustment GL'}
                              onChange={e => updateLine(line._key, 'adjustmentGL', e.target.value)}>
                              {glAccountOptions(glAccounts)}
                            </TextField>
                        }
                      </TableCell>

                      {/* Amount */}
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {(line.amount || 0).toFixed(2)}
                      </TableCell>

                      {/* Delete */}
                      <TableCell>
                        {!posted && (
                          <IconButton size="small" color="error" onClick={() => removeLine(line._key)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {!posted && (
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small">
                Add Line
              </Button>
              <Typography variant="body2" color="text.secondary">
                {lines.length} line{lines.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}

          {!posted && (
            <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
              <Button onClick={() => navigate('/payables/item-journals')}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ItemJournalForm;
